import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  userId: string;
  stripePaymentMethodId: string;
  brand: string;      // visa | mastercard | amex | discover | ...
  last4: string;
  expMonth: number;
  expYear: number;
  billingName?: string;
  billingPostalCode?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetupIntentResponse {
  clientSecret: string;
  customerId: string;
}

// ── API Functions ────────────────────────────────────────────────────────────

/**
 * List all saved payment methods for the current user.
 */
export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  return await api.get<PaymentMethod[]>('/v1/payment-methods');
};

/**
 * Create a Stripe SetupIntent so the frontend can securely capture card details.
 * Returns { clientSecret, customerId } — pass clientSecret to Stripe.js confirmSetup().
 */
export const createSetupIntent = async (): Promise<SetupIntentResponse> => {
  return await api.post<SetupIntentResponse>('/v1/payment-methods/setup', {});
};

/**
 * After Stripe confirms the SetupIntent, attach the PaymentMethod to the user's profile.
 * Also accepts safe card metadata (brand, last4, expMonth, expYear) as a fallback
 * for dev/test environments where Stripe is not fully configured.
 */
export const attachPaymentMethod = async (data: {
  stripePaymentMethodId: string;
  isDefault?: boolean;
  // Safe fallback metadata (never raw card number):
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  billingName?: string;
}): Promise<PaymentMethod> => {
  return await api.post<PaymentMethod>('/v1/payment-methods/attach', data);
};

/**
 * Make a saved card the default payment method.
 */
export const setDefaultPaymentMethod = async (id: string): Promise<PaymentMethod> => {
  return await api.patch<PaymentMethod>(`/v1/payment-methods/${id}/default`, {});
};

/**
 * Remove (detach) a saved card.  The backend will auto-promote the oldest
 * remaining card to default if the deleted card was the default.
 */
export const deletePaymentMethod = async (id: string): Promise<void> => {
  return await api.delete<void>(`/v1/payment-methods/${id}`);
};
